import { useBehavior, useTriggerAction, camelCaseToKebabCase, getFieldName } from '@libs/core';
import { TInputData, TWidgetProps } from '@libs/types';
import React from 'react';
import { useDateInput } from '../../hooks/useDateInput';
import { TInputOption } from '../../types/options';
import { InputWrapper } from '../UI/Wrapper/InputWrapper';
import { useInputMask } from '../../hooks/useInputs';

const isValidDateMask = (mask: string): boolean => {
    if (!mask) return false;

    const lowerMask = mask.toLowerCase();
    const hasDd = lowerMask.includes('dd');
    const hasMm = lowerMask.includes('mm');
    const hasYyyy = lowerMask.includes('yyyy');

    return hasDd && hasMm && hasYyyy;
};

export const InputDate = ({ widget }: TWidgetProps<TInputData, TInputOption>) => {
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { isFocused, isFilled, isError, errorMessage, label, helperText, handleChange, handleBlur, handleFocus } = useDateInput(widget, {
        isDisabled,
        isHidden,
        triggerActionsByType,
    });
    const options = widget.options ?? {};
    const { mask = '', tabIndex, mandatory } = widget.data || {};
    const inputFormat = isValidDateMask(mask) ? mask : undefined;
    const ref = useInputMask(undefined, undefined, undefined, true, false, 'datetime', inputFormat);
    const name = getFieldName(options.autofill);
    const value = (widget?.data?.value as string) ?? '';

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        handleChange?.(newValue);
    };

    return (
        <InputWrapper
            dataTest={widget.uid}
            widget={widget}
            isDisabled={isDisabled}
            isHidden={isHidden}
            labelText={label}
            isFocused={isFocused}
            isError={isError}
            errorMessage={errorMessage}
            helperText={helperText}
            isFilled={isFilled}
            marginTop={options.margin?.top}
            marginRight={options.margin?.right}
            marginBottom={options.margin?.bottom}
            marginLeft={options.margin?.left}
        >
            <input
                // do not apply mask if we do not have inputFormat
                ref={inputFormat ? ref : undefined}
                value={value}
                name={name}
                autoComplete={camelCaseToKebabCase(options.autofill ?? 'off')}
                autoFocus={options.onFocus || false}
                type="text"
                inputMode="numeric"
                readOnly={isDisabled}
                required={mandatory}
                maxLength={options.maxLength}
                tabIndex={tabIndex}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onChange={handleInputChange}
            />
        </InputWrapper>
    );
};
